// src/pages/AuthErrorPage.js
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import './LoginPage.css';

/**
 * Authentication Error Page
 * Displays user-friendly error messages when authentication fails
 */
const AuthErrorPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // Get error details from URL params or location state
  const errorCode = params.get('error') || location.state?.error || 'unknown_error';
  const errorDescription = params.get('error_description') || location.state?.errorDescription;

  // Map error codes to user-friendly messages
  const getErrorInfo = (code) => {
    const errorMap = {
      redirect_mismatch: {
        title: 'Configuration Error',
        message: 'The authentication service is temporarily misconfigured.',
        suggestion: 'Please try again later or contact support if the problem persists.'
      },
      access_denied: {
        title: 'Access Denied',
        message: 'You do not have permission to access this resource.',
        suggestion: 'Please contact support if you believe this is an error.'
      },
      invalid_request: {
        title: 'Invalid Request',
        message: 'The authentication request was invalid.',
        suggestion: 'Please try signing in again.'
      },
      unauthorized_client: {
        title: 'Service Unavailable',
        message: 'The authentication service is temporarily unavailable.',
        suggestion: 'Please try again later.'
      },
      server_error: {
        title: 'Server Error',
        message: 'The authentication server encountered an error.',
        suggestion: 'Please try again later.'
      },
      redirect_failure: {
        title: 'Sign In Failed',
        message: 'Unable to complete the sign in process.',
        suggestion: 'Please try again. If the problem persists, contact support.'
      },
      temporarily_unavailable: {
        title: 'Service Unavailable',
        message: 'The authentication service is temporarily unavailable.',
        suggestion: 'Please try again in a few minutes.'
      },
      network_error: {
        title: 'Connection Error',
        message: 'Unable to connect to the authentication service.',
        suggestion: 'Please check your internet connection and try again.'
      },
      session_expired: {
        title: 'Session Expired',
        message: 'Your session has expired.',
        suggestion: 'Please sign in again to continue.'
      },
      unknown_error: {
        title: 'Authentication Error',
        message: 'An unexpected error occurred during authentication.',
        suggestion: 'Please try again later.'
      }
    };

    return errorMap[code] || errorMap.unknown_error;
  };

  const errorInfo = getErrorInfo(errorCode);

  return (
    <div className="login-page">
      <div className="page-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
      </div>

      <section className="login-section">
        <div className="section-container">
          <motion.div
            className="login-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Error Icon */}
            <div className="login-icon warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1>{errorInfo.title}</h1>

            <p className="login-description">
              {errorInfo.message}
            </p>

            <p className="login-description" style={{ marginTop: '0.5rem', opacity: 0.8 }}>
              {errorInfo.suggestion}
            </p>

            {/* Show technical details in development */}
            {process.env.NODE_ENV === 'development' && errorDescription && (
              <div className="login-error" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                <strong>Details:</strong> {errorDescription}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <Link to="/login" className="login-button" style={{ textDecoration: 'none', textAlign: 'center' }}>
                Try Again
              </Link>

              <Link to="/" className="login-back">
                Return to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AuthErrorPage;
